import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Ticket, 
  Users, 
  Wrench,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  Plus,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Loader2,
  ClipboardCheck,
  FileText,
  FileWarning,
  Bell,
  ArrowRight,
  Activity,
  ShieldAlert,
  PackageX,
  UserX,
  Upload
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, formatDate, formatDateTime, formatRelativeTime, getStatusColor, getPriorityColor } from '@/lib/utils';
import api from '@/services/api';

// Types
interface AssetStatistics {
  total: number;
  warrantyExpiring: number;
  byStatus: Array<{ status: string; count: number }>;
  byCategory: Array<{ categoryId: string; categoryName: string; count: number }>;
  byBranch: Array<{ branchId: string; branchName: string; count: number }>;
}

interface TicketStatistics {
  overview: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
    slaBreached: number;
  };
  byPriority: Array<{ priority: string; _count: number }>;
  byCategory: Array<{ categoryId: string; _count: number }>;
  recentTickets: Array<{
    id: string;
    ticketNumber: string;
    title: string;
    status: string;
    priority: string;
    createdAt: string;
    category?: { name: string };
    requester?: { firstName: string; lastName: string };
  }>;
}

interface EmployeeCount {
  total: number;
}

interface DashboardData {
  systemHealth: string;
  uptime: string;
  lastBackup: string;
}

// Sanalam-specific types
interface WorkflowStats {
  pendingAccessForms: number;
  pendingAssetIssueForms: number;
  assetsInRepair: number;
  expiringDocuments: number;
}

interface Alert {
  type: 'sla_breach' | 'low_stock' | 'warranty_expiring' | 'document_expiring' | 'notorious_employee';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  count?: number;
  link?: string;
}

interface RecentActivity {
  id: string;
  type: 'access_form' | 'asset_transfer' | 'repair_completed';
  title: string;
  description: string;
  timestamp: string;
  link?: string;
}

interface SanalamDashboardData {
  workflowStats: WorkflowStats;
  alerts: Alert[];
  recentActivity: {
    accessForms: RecentActivity[];
    assetTransfers: RecentActivity[];
    repairsCompleted: RecentActivity[];
  };
}

// Chart Colors
const STATUS_COLORS: Record<string, string> = {
  AVAILABLE: '#22c55e',
  ASSIGNED: '#3b82f6',
  DEPLOYED: '#3b82f6',
  IN_STOCK: '#22c55e',
  IN_MAINTENANCE: '#eab308',
  UNDER_REPAIR: '#eab308',
  RETIRED: '#6b7280',
  DISPOSED: '#ef4444',
  LOST: '#dc2626',
  RESERVED: '#a855f7',
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: '#6b7280',
  MEDIUM: '#3b82f6',
  HIGH: '#f97316',
  URGENT: '#ef4444',
  CRITICAL: '#dc2626',
};

// Stats Card Component
function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  trend,
  trendValue,
  color,
  loading = false,
  onClick
}: { 
  title: string; 
  value: number | string; 
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  return (
    <Card 
      className={cn(
        "hover:shadow-lg transition-all duration-200",
        onClick && "cursor-pointer hover:scale-[1.02] hover:border-primary/50"
      )}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {loading ? (
              <div className="h-9 flex items-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <p className="text-3xl font-bold mt-1">{value}</p>
            )}
            {trendValue && !loading && (
              <p className={cn(
                "text-xs mt-1 flex items-center gap-1",
                trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-muted-foreground'
              )}>
                {trend === 'up' && <TrendingUp className="w-3 h-3" />}
                {trend === 'down' && <TrendingDown className="w-3 h-3" />}
                {trendValue}
              </p>
            )}
          </div>
          <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", color)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Error State Component
function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
      <p className="text-muted-foreground mb-4">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      )}
    </div>
  );
}

// Custom Tooltip for Charts
function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3">
        <p className="font-medium">{label || payload[0].name}</p>
        <p className="text-sm text-muted-foreground">
          Count: <span className="font-semibold">{payload[0].value}</span>
        </p>
      </div>
    );
  }
  return null;
}

export default function DashboardPage() {
  const navigate = useNavigate();

  // Fetch asset statistics
  const { 
    data: assetStats, 
    isLoading: assetLoading, 
    error: assetError,
    refetch: refetchAssets 
  } = useQuery<AssetStatistics>({
    queryKey: ['asset-statistics'],
    queryFn: async () => {
      const response = await api.get('/assets/stats');
      return response.data?.data || response.data || {};
    },
    staleTime: 30000,
    retry: 2,
  });

  // Fetch ticket statistics
  const { 
    data: ticketStats, 
    isLoading: ticketLoading, 
    error: ticketError,
    refetch: refetchTickets 
  } = useQuery<TicketStatistics>({
    queryKey: ['ticket-statistics'],
    queryFn: async () => {
      const response = await api.get('/tickets/stats');
      return response.data?.data || response.data || {};
    },
    staleTime: 30000,
    retry: 2,
  });

  // Fetch employee count
  const { 
    data: employeeData, 
    isLoading: employeeLoading 
  } = useQuery<EmployeeCount>({
    queryKey: ['employee-count'],
    queryFn: async () => {
      const response = await api.get('/employees', { params: { limit: 1 } });
      return { total: response.data.pagination?.total || 0 };
    },
    staleTime: 60000,
    retry: 2,
  });

  // Fetch system dashboard data
  const { data: dashboardData } = useQuery<DashboardData>({
    queryKey: ['system-dashboard'],
    queryFn: async () => {
      const response = await api.get('/system/dashboard');
      return response.data?.data || response.data || {};
    },
    staleTime: 60000,
    retry: 1,
  });

  // Fetch Sanalam-specific dashboard data
  const { 
    data: sanalamData,
    isLoading: sanalamLoading,
    refetch: refetchSanalam
  } = useQuery<SanalamDashboardData>({
    queryKey: ['sanalam-dashboard'],
    queryFn: async () => {
      const response = await api.get('/dashboard');
      return response.data?.data || response.data || {};
    },
    staleTime: 30000,
    retry: 2,
  });

  // Prepare chart data
  const assetStatusData = assetStats?.byStatus?.map(item => ({
    name: item.status.replace(/_/g, ' '),
    value: item.count,
    fill: STATUS_COLORS[item.status] || '#6b7280'
  })) || [];

  const ticketPriorityData = ticketStats?.byPriority?.map(item => ({
    name: item.priority,
    count: item._count,
    fill: PRIORITY_COLORS[item.priority] || '#6b7280'
  })) || [];

  // Get recent tickets (last 5)
  const recentTickets = ticketStats?.recentTickets?.slice(0, 5) || [];

  // Calculate asset counts by status
  const assetsByStatus = assetStats?.byStatus?.reduce((acc, item) => {
    acc[item.status] = item.count;
    return acc;
  }, {} as Record<string, number>) || {};

  // Combined repair count - use RepairRecord count as the primary source (active repairs)
  // This is more accurate than asset status since status might not be updated
  const activeRepairsCount = sanalamData?.workflowStats?.assetsInRepair ?? 0;

  const isLoading = assetLoading || ticketLoading || employeeLoading || sanalamLoading;

  // Last updated timestamp
  const lastUpdated = new Date();

  const handleRefreshAll = () => {
    refetchAssets();
    refetchTickets();
    refetchSanalam();
  };

  // Helper to get alert icon
  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'sla_breach': return ShieldAlert;
      case 'low_stock': return PackageX;
      case 'warranty_expiring': return Calendar;
      case 'document_expiring': return FileWarning;
      case 'notorious_employee': return UserX;
      default: return AlertTriangle;
    }
  };

  // Helper to get alert severity color
  const getAlertSeverityColor = (severity: Alert['severity']) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper to get activity icon
  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'access_form': return ClipboardCheck;
      case 'asset_transfer': return ArrowRight;
      case 'repair_completed': return Wrench;
      default: return Activity;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome to your asset management dashboard
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Updated: {formatDateTime(lastUpdated)}
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefreshAll}
            disabled={isLoading}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate('/assets/new')} className="gap-2">
          <Plus className="w-4 h-4" />
          New Asset
        </Button>
        <Button onClick={() => navigate('/tickets/new')} variant="secondary" className="gap-2">
          <Plus className="w-4 h-4" />
          New Ticket
        </Button>
        <Button onClick={() => navigate('/employees/new')} variant="outline" className="gap-2">
          <Plus className="w-4 h-4" />
          New Employee
        </Button>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Assets"
          value={assetStats?.total ?? 0}
          icon={Package}
          color="bg-blue-500"
          loading={assetLoading}
          trend="up"
          trendValue="All tracked assets"
          onClick={() => navigate('/assets')}
        />
        <StatCard
          title="Active Tickets"
          value={(ticketStats?.overview?.open ?? 0) + (ticketStats?.overview?.inProgress ?? 0)}
          icon={Ticket}
          color="bg-yellow-500"
          loading={ticketLoading}
          trend={ticketStats?.overview?.slaBreached ? 'down' : 'neutral'}
          trendValue={ticketStats?.overview?.slaBreached ? `${ticketStats.overview.slaBreached} SLA breached` : 'Within SLA'}
          onClick={() => navigate('/tickets')}
        />
        <StatCard
          title="Total Employees"
          value={employeeData?.total ?? 0}
          icon={Users}
          color="bg-green-500"
          loading={employeeLoading}
          trendValue="Active users"
          onClick={() => navigate('/employees')}
        />
        <StatCard
          title="Assets in Repair"
          value={activeRepairsCount}
          icon={Wrench}
          color="bg-orange-500"
          loading={sanalamLoading}
          trendValue="Active repair jobs"
          onClick={() => navigate('/repairs')}
        />
      </div>

      {/* Workflow Status Cards - Sanalam Specific */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-500"
          onClick={() => navigate('/access-forms')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Access Forms</p>
                {sanalamLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mt-1" />
                ) : (
                  <p className="text-2xl font-bold">{sanalamData?.workflowStats?.pendingAccessForms ?? 0}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Awaiting HR/IT approval</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-indigo-500"
          onClick={() => navigate('/asset-issue-forms')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Issue Forms</p>
                {sanalamLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mt-1" />
                ) : (
                  <p className="text-2xl font-bold">{sanalamData?.workflowStats?.pendingAssetIssueForms ?? 0}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Awaiting signatures</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={cn(
            "hover:shadow-lg transition-shadow cursor-pointer border-l-4",
            (ticketStats?.overview?.slaBreached ?? 0) > 0 ? "border-l-red-500" : "border-l-green-500"
          )}
          onClick={() => navigate('/tickets?filter=sla-breached')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">SLA Breaches</p>
                {ticketLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mt-1" />
                ) : (
                  <p className={cn(
                    "text-2xl font-bold",
                    (ticketStats?.overview?.slaBreached ?? 0) > 0 ? "text-red-600" : "text-green-600"
                  )}>
                    {ticketStats?.overview?.slaBreached ?? 0}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {(ticketStats?.overview?.slaBreached ?? 0) > 0 ? "Tickets overdue" : "All within SLA"}
                </p>
              </div>
              <div className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center",
                (ticketStats?.overview?.slaBreached ?? 0) > 0 ? "bg-red-100" : "bg-green-100"
              )}>
                <ShieldAlert className={cn(
                  "w-5 h-5",
                  (ticketStats?.overview?.slaBreached ?? 0) > 0 ? "text-red-600" : "text-green-600"
                )} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-rose-500"
          onClick={() => navigate('/documents')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Expiring Documents</p>
                {sanalamLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mt-1" />
                ) : (
                  <p className="text-2xl font-bold">{sanalamData?.workflowStats?.expiringDocuments ?? 0}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Expiring in 30 days</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                <FileWarning className="w-5 h-5 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Assets by Status - Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Assets by Status
            </CardTitle>
            <CardDescription>Distribution of assets across different statuses</CardDescription>
          </CardHeader>
          <CardContent>
            {assetLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : assetError ? (
              <ErrorState 
                message="Failed to load asset statistics" 
                onRetry={() => refetchAssets()} 
              />
            ) : assetStatusData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No asset data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={assetStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {assetStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Tickets by Priority - Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              Tickets by Priority
            </CardTitle>
            <CardDescription>Current ticket distribution by priority level</CardDescription>
          </CardHeader>
          <CardContent>
            {ticketLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : ticketError ? (
              <ErrorState 
                message="Failed to load ticket statistics" 
                onRetry={() => refetchTickets()} 
              />
            ) : ticketPriorityData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No ticket data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={ticketPriorityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    radius={[4, 4, 0, 0]}
                  >
                    {ticketPriorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Recent Tickets & Warranty Expirations */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Tickets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Tickets
              </CardTitle>
              <CardDescription>Latest 5 support tickets</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tickets')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {ticketLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-muted"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : ticketError ? (
              <ErrorState 
                message="Failed to load tickets" 
                onRetry={() => refetchTickets()} 
              />
            ) : recentTickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tickets yet</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/tickets/new')}
                >
                  Create your first ticket
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <div 
                    key={ticket.id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{ticket.ticketNumber}: {ticket.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {ticket.requester?.firstName} {ticket.requester?.lastName} • {formatDate(ticket.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={cn("px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap", getPriorityColor(ticket.priority))}>
                        {ticket.priority}
                      </span>
                      <span className={cn("px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap", getStatusColor(ticket.status))}>
                        {ticket.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Warranty Expirations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Warranty Expirations
              </CardTitle>
              <CardDescription>Assets expiring in the next 30 days</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/warranty')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {assetLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="h-10 w-10 rounded bg-muted"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : assetError ? (
              <ErrorState 
                message="Failed to load warranty data" 
                onRetry={() => refetchAssets()} 
              />
            ) : (assetStats?.warrantyExpiring ?? 0) === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium text-green-600">All Clear!</p>
                <p className="text-sm">No warranties expiring soon</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-orange-500" />
                <p className="font-bold text-2xl text-orange-600">{assetStats?.warrantyExpiring}</p>
                <p className="text-muted-foreground">assets with warranties expiring</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/reports')}
                >
                  View warranty report
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Alerts/Quick Actions - Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Activity Section - Left (2 columns) */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>Latest form submissions, transfers, and repairs</CardDescription>
            </CardHeader>
            <CardContent>
              {sanalamLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="h-8 w-8 rounded-full bg-muted"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Recent Access Forms */}
                  {sanalamData?.recentActivity?.accessForms && sanalamData.recentActivity.accessForms.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" />
                        Access Form Submissions
                      </h4>
                      <div className="space-y-2">
                        {sanalamData.recentActivity.accessForms.slice(0, 3).map((activity) => {
                          const Icon = getActivityIcon(activity.type);
                          return (
                            <div
                              key={activity.id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => activity.link && navigate(activity.link)}
                            >
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{activity.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatRelativeTime(activity.timestamp)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recent Asset Transfers */}
                  {sanalamData?.recentActivity?.assetTransfers && sanalamData.recentActivity.assetTransfers.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4" />
                        Asset Transfers
                      </h4>
                      <div className="space-y-2">
                        {sanalamData.recentActivity.assetTransfers.slice(0, 3).map((activity) => {
                          const Icon = getActivityIcon(activity.type);
                          return (
                            <div
                              key={activity.id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => activity.link && navigate(activity.link)}
                            >
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{activity.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatRelativeTime(activity.timestamp)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recent Repairs Completed */}
                  {sanalamData?.recentActivity?.repairsCompleted && sanalamData.recentActivity.repairsCompleted.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        Repairs Completed
                      </h4>
                      <div className="space-y-2">
                        {sanalamData.recentActivity.repairsCompleted.slice(0, 3).map((activity) => {
                          const Icon = getActivityIcon(activity.type);
                          return (
                            <div
                              key={activity.id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => activity.link && navigate(activity.link)}
                            >
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-green-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{activity.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatRelativeTime(activity.timestamp)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* No activity message */}
                  {(!sanalamData?.recentActivity?.accessForms?.length &&
                    !sanalamData?.recentActivity?.assetTransfers?.length &&
                    !sanalamData?.recentActivity?.repairsCompleted?.length) && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No recent activity</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alerts & Quick Actions - Right (1 column) */}
        <div className="space-y-6">
          {/* Alerts Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Alerts
              </CardTitle>
              <CardDescription>Items requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {sanalamLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse h-16 bg-muted/50 rounded-lg"></div>
                  ))}
                </div>
              ) : sanalamData?.alerts && sanalamData.alerts.length > 0 ? (
                <div className="space-y-2">
                  {sanalamData.alerts.map((alert, index) => {
                    const Icon = getAlertIcon(alert.type);
                    return (
                      <div
                        key={index}
                        className={cn(
                          "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow",
                          getAlertSeverityColor(alert.severity)
                        )}
                        onClick={() => alert.link && navigate(alert.link)}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{alert.title}</p>
                            <p className="text-xs opacity-80">{alert.message}</p>
                          </div>
                          {alert.count && (
                            <span className="text-lg font-bold">{alert.count}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium text-green-600">All Clear!</p>
                  <p className="text-xs">No alerts at this time</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Quick Actions
              </CardTitle>
              <CardDescription>Common tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => navigate('/access-forms?action=new')}
                >
                  <ClipboardCheck className="w-5 h-5" />
                  <span className="text-xs">Access Form</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => navigate('/asset-issue-forms?action=new')}
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">Issue Form</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => navigate('/repairs?action=new')}
                >
                  <Wrench className="w-5 h-5" />
                  <span className="text-xs">Log Repair</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => navigate('/documents?action=upload')}
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-xs">Upload Doc</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Asset Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Asset Status Overview</CardTitle>
          <CardDescription>Distribution of assets by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
            <div 
              className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:shadow-md transition-all cursor-pointer hover:scale-105"
              onClick={() => navigate('/assets?status=ASSIGNED')}
            >
              <Users className="w-8 h-8 mx-auto text-blue-600" />
              <p className="text-2xl font-bold mt-2">
                {assetLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (assetsByStatus['ASSIGNED'] ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">Assigned</p>
            </div>
            <div 
              className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:shadow-md transition-all cursor-pointer hover:scale-105"
              onClick={() => navigate('/assets?status=IN_STOCK')}
            >
              <CheckCircle className="w-8 h-8 mx-auto text-green-600" />
              <p className="text-2xl font-bold mt-2">
                {assetLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (assetsByStatus['IN_STOCK'] ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">In Stock</p>
            </div>
            <div 
              className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg hover:shadow-md transition-all cursor-pointer hover:scale-105"
              onClick={() => navigate('/assets?status=AVAILABLE')}
            >
              <Package className="w-8 h-8 mx-auto text-emerald-600" />
              <p className="text-2xl font-bold mt-2">
                {assetLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (assetsByStatus['AVAILABLE'] ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">Available</p>
            </div>
            <div 
              className="text-center p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg hover:shadow-md transition-all cursor-pointer hover:scale-105"
              onClick={() => navigate('/assets?status=DEPLOYED')}
            >
              <Package className="w-8 h-8 mx-auto text-cyan-600" />
              <p className="text-2xl font-bold mt-2">
                {assetLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (assetsByStatus['DEPLOYED'] ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">Deployed</p>
            </div>
            <div 
              className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:shadow-md transition-all cursor-pointer hover:scale-105"
              onClick={() => navigate('/assets')}
            >
              <Package className="w-8 h-8 mx-auto text-purple-600" />
              <p className="text-2xl font-bold mt-2">
                {assetLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (assetStats?.total ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </div>
          {/* Active Repairs Note */}
          {activeRepairsCount > 0 && (
            <div 
              className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 flex items-center justify-between cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
              onClick={() => navigate('/repairs')}
            >
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {activeRepairsCount} asset{activeRepairsCount > 1 ? 's' : ''} currently in repair workflow
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-yellow-600" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Status Footer */}
      {dashboardData && (
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span>System: {dashboardData.systemHealth || 'Healthy'}</span>
              </div>
              {dashboardData.uptime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Uptime: {dashboardData.uptime}</span>
                </div>
              )}
              {dashboardData.lastBackup && (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  <span>Last Backup: {formatDate(dashboardData.lastBackup)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
